import React, { useRef, useEffect, useState, useMemo } from 'react'
import PropTypes from 'prop-types'

import useCells from './useCells'

import './Table.scss'
import { compareTuples } from '@aytm/app/insights/dashboard/lib/comparison'

const CLASS = 'insights-portal-table'

const tablePropsComparer = compareTuples(({ rows, fixedColumns, spanRowIndex, renderSpan, blockWidth, group }) => [
  rows,
  fixedColumns,
  spanRowIndex,
  renderSpan,
  blockWidth,
  group
])
const propsAreEqual = (prev, cur) =>
  cur.wait && prev.blockWidth === cur.blockWidth ? true : tablePropsComparer(prev, cur)

const InnerTable = (props, scrollRef) => {
  const className = props.className || CLASS

  const groupSize = props.group.count || 0

  const containerRef = useRef()

  const changeShowFromToRef = useRef()
  changeShowFromToRef.current = props.onChangeShowFromTo

  const lastShowFromTo = useRef({ itemFrom: -1, itemTo: -1, groupFrom: -1, groupTo: -1 })

  const [clientWidth, setClientWidth] = useState(0)
  const [scrollLeft, setScrollLeft] = useState(0)

  const { templateColumns, rowCount, colCount, getElement, showFrom, showTo } = useCells({
    ...props,
    className,
    clientWidth,
    scrollLeft,
    spanColIndex: 1
  })

  const items = useMemo(() => {
    const items = []
    for (let row = 0; row < rowCount; row++) {
      for (let col = 0; col < colCount; col++) {
        const element = getElement(row, col, `${row}-${col}`)
        if (element) {
          items.push(element)
        }
      }
    }
    return items
  }, [colCount, getElement, rowCount])

  const bodyStyle = useMemo(
    () =>
      Object({
        display: 'grid',
        gridTemplateColumns: templateColumns
      }),
    [templateColumns]
  )

  useEffect(() => {
    const updateContainerWidth = () => {
      if (containerRef.current) {
        setClientWidth(containerRef.current.clientWidth)
        setScrollLeft(containerRef.current.scrollLeft)
      }
    }
    updateContainerWidth()
  }, [props.blockWidth])

  useEffect(() => {
    if (changeShowFromToRef.current && clientWidth > 0) {
      const itemFrom = showFrom - 2
      const itemTo = showTo - 2
      const groupFrom = groupSize > 0 ? Math.trunc(itemFrom / groupSize) : -1
      const groupTo = groupSize > 0 ? Math.trunc((itemTo - groupSize + 1) / groupSize) : -1
      if (
        lastShowFromTo.current.itemFrom !== itemFrom ||
        lastShowFromTo.current.itemTo !== itemTo ||
        lastShowFromTo.current.groupFrom !== groupFrom ||
        lastShowFromTo.current.groupTo !== groupTo
      ) {
        lastShowFromTo.current.itemFrom = itemFrom
        lastShowFromTo.current.itemTo = itemTo
        lastShowFromTo.current.groupFrom = groupFrom
        lastShowFromTo.current.groupTo = groupTo
        changeShowFromToRef.current({ itemFrom, itemTo, groupFrom, groupTo })
      }
    }
  }, [showFrom, showTo, clientWidth, groupSize])

  const containerStyle = { width: `${props.blockWidth}px` }

  // This effect is to add a passive scroll event listener to the container
  // onScroll property is not good enough (not passive)
  const scrollTick = useRef({ pending: false, latest: 0 })
  useEffect(() => {
    const el = containerRef.current
    if (!el) return void 0
    const handleScroll = event => {
      if (event.target && event.target === containerRef.current) {
        const x = event.target.scrollLeft
        scrollTick.current.latest = x
        if (!scrollTick.current.pending) {
          scrollTick.current.pending = true
          requestAnimationFrame(() => {
            setScrollLeft(scrollTick.current.latest)
            scrollTick.current.pending = false
          })
        }
      }
    }
    el.addEventListener('scroll', handleScroll, { passive: true })
    return () => el.removeEventListener('scroll', handleScroll)
  }, [])

  return (
    <div className={className} style={containerStyle} ref={containerRef}>
      <div className={`${className}-body`} style={bodyStyle} ref={scrollRef}>
        {items}
      </div>
    </div>
  )
}

const Table = React.forwardRef(InnerTable)

InnerTable.displayName = 'Table'

InnerTable.propTypes = {
  className: PropTypes.string,
  wait: PropTypes.bool,
  rows: PropTypes.arrayOf(PropTypes.array),
  layout: PropTypes.array,
  captionWidth: PropTypes.number,
  columnWidth: PropTypes.number,
  extraColumnWidth: PropTypes.number,
  gapWidth: PropTypes.number,
  gapHeight: PropTypes.number,
  fixedColumns: PropTypes.number,
  extraColumns: PropTypes.number,
  spanRowIndex: PropTypes.number,
  splitterWidth: PropTypes.number,
  blockWidth: PropTypes.number,
  renderSpan: PropTypes.func,
  group: PropTypes.object,
  onHover: PropTypes.func,
  onChangeShowFromTo: PropTypes.func
}

InnerTable.defaultProps = {
  rows: [],
  layout: [],
  captionWidth: 220,
  columnWidth: 160,
  extraColumnWidth: 160,
  gapWidth: 12,
  gapHeight: 12,
  fixedColumns: 0,
  extraColumns: 0,
  spanRowIndex: -1,
  splitterWidth: 3,
  blockWidth: 0,
  group: {}
}

export default React.memo(Table, propsAreEqual)
